import { httpClient } from "@bw/shared/api"
import type { components } from "@bw/shared/types/generated"

export type CoachIncome = components["schemas"]["CoachIncomeResponse"]

/** Self-scoped recorded UTC movements; no caller-selected coach identity. */
export function getCoachIncome(month?: string): Promise<CoachIncome> {
  const query = month ? `?month=${encodeURIComponent(month)}` : ""
  return httpClient.get<CoachIncome>(`/coaching/income${query}`)
}

// ---------------------------------------------------------------------------
// Coach availability — GET/PUT /coaching/club-profiles/{profile_id}/availability
//
// A coach's weekly available time windows. USER-SCOPED: the FE passes the
// CALLER's own uid as coach_id (the backend authorizes that a coach sets their
// OWN availability — set_availability(coach_id, caller.user_id, body)).
//
// day_of_week is 0=Mon … 6=Sun (per common/models.py
// CoachAvailabilitySlotCreate `Field(..., ge=0, le=6, description="0=Mon, 6=Sun")`)
// — NOT the JS getDay() 0=Sun convention. The client passes the int through
// verbatim; the renderer maps it to a weekday name.
//
// PUT is REPLACE-ALL: the body is the full slots array sent DIRECTLY (a bare
// List[CoachAvailabilitySlotCreate], NOT a `{ slots: [...] }` wrapper — mirrors
// facilityApi.setOperatingHours), and the server returns the new full list as the
// source of truth post-save.
// ---------------------------------------------------------------------------

// Response shape — mirrors generated `CoachAvailabilitySlotResponse`. Times are
// the server's "HH:MM" strings; facility_id is an optional uuid (null for a
// facility-agnostic window).
export type CoachAvailabilitySlot = components["schemas"]["CoachAvailabilitySlotResponse"]

// Create/replace shape — mirrors generated `CoachAvailabilitySlotCreate`.
export type CoachAvailabilitySlotInput = components["schemas"]["CoachAvailabilitySlotCreate"]

// GET /coaching/club-profiles/{profileId}/availability — exact facility slots,
// order preserved (no reshaping/reordering).
export function getAvailability(coachProfileId: string, facilityId: string): Promise<CoachAvailabilitySlot[]> {
  return httpClient.get<CoachAvailabilitySlot[]>(
    `/coaching/club-profiles/${encodeURIComponent(coachProfileId)}/availability?facility_id=${encodeURIComponent(facilityId)}`,
  )
}

// PUT /coaching/club-profiles/{profileId}/availability — replace-all. The slots array is
// the body DIRECTLY (no wrapper); an empty array clears all availability. Returns
// the new full list the server replied with.
//
// Optimistic concurrency (#1386): the replace-all is a blind delete-all-then-write,
// so two operators editing the same coach concurrently lose-update — a stale
// session's no-edit Save silently wipes slots saved elsewhere. When the caller
// passes `ifUnmodifiedSince` (the MAX `updated_at` from its last GET — the snapshot
// it's editing against), it rides as the `If-Unmodified-Since` request header; the
// backend rejects with a structured 412 (`code: "AVAILABILITY_CONFLICT"`) when the
// stored state is NEWER than that token, so the stale write is refused rather than
// clobbering. Omitting it preserves the old replace-all (first write / empty load).
export function setAvailability(
  coachProfileId: string,
  slots: CoachAvailabilitySlotInput[],
  ifUnmodifiedSince?: string,
): Promise<CoachAvailabilitySlot[]> {
  return httpClient.put<CoachAvailabilitySlot[]>(
    `/coaching/club-profiles/${encodeURIComponent(coachProfileId)}/availability`,
    slots,
    ifUnmodifiedSince
      ? { headers: { "If-Unmodified-Since": ifUnmodifiedSince } }
      : undefined,
  )
}
